import { CSSProperties } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { VisuallyHidden } from 'react-aria';
import styled, { css } from 'styled-components';
import { HiddenTab } from '../shared';
import { breakpoints } from '../../../global/breakpoints';
import { PointerOnlyTooltip } from '../../tooltip';

/**
 * A tab menu component that displays tabs as a button group.
 * The styling mimics Primer's SegmentedControl component, with an in-house custom
 * vertical layout for narrow viewports.
 *
 * @see ButtonNavContainer for a version that works with react-router-dom.
 *
 * @example
 * <ButtonMenuContainer>
 *   <ButtonMenuTabList ariaLabel="Segmented control navigation">
 *     <ButtonMenuTabListItem id="tab-1">Tab 1 name</ButtonMenuTabListItem>
 *     <ButtonMenuTabListItem id="tab-2">Tab 2 name</ButtonMenuTabListItem>
 *   </ButtonMenuTabList>
 *   <ButtonMenuTabContent id="tab-1">Content of Tab 1</ButtonMenuTabContent>
 *   <ButtonMenuTabContent id="tab-2">Content of Tab 2</ButtonMenuTabContent>
 * </ButtonMenuContainer>
 *
 */
export const ButtonMenuContainer = ({
  children,
  onSelectionChange,
  selectedKey,
  defaultSelectedKey,
}: React.PropsWithChildren<{
  onSelectionChange?: React.ComponentProps<typeof Tabs>['onSelectionChange'];
  selectedKey?: React.ComponentProps<typeof Tabs>['selectedKey'];
  defaultSelectedKey?: React.ComponentProps<typeof Tabs>['defaultSelectedKey'];
}>) => {
  return (
    <StyledTabs
      onSelectionChange={onSelectionChange}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      keyboardActivation="manual"
    >
      {children}
    </StyledTabs>
  );
};

/**
 * Tab list component that can optionally accept an `id` prop for HTML identification.
 * The `id` prop was added to support accessibility and testability requirements,
 * allowing specific tab lists to be identified in the DOM for testing and tracking purposes.
 * This is particularly useful for distinguishing between multiple tab lists on the same page.
 */
export const ButtonMenuTabList = ({
  ariaLabel,
  children,
  collapseWidth,
  variant = 'default',
  fontSize,
  tabPadding,
  style,
  id,
}: React.PropsWithChildren<{
  ariaLabel: string;
  collapseWidth?: string;
  variant?: 'pill' | 'default';
  fontSize?: 'xs' | 'sm';
  tabPadding?: string;
  style?: CSSProperties;
  id?: string;
}>) => {
  return (
    <StyledList
      style={style}
      aria-label={ariaLabel}
      $collapseWidth={collapseWidth}
      $variant={variant}
      $fontSize={fontSize}
      $tabPadding={tabPadding}
      id={id}
    >
      {children}
    </StyledList>
  );
};

type DisabledTabItemProps = {
  isDisabled?: boolean;
  tooltipText?: string;
};

export const MaybeDisabledButtonMenuTabListItem = ({
  children,
  id,
  isHidden,
  isDisabled,
  tooltipText,
}: React.PropsWithChildren<
  {
    id: React.ComponentProps<typeof Tab>['id'];
    isHidden?: boolean;
  } & DisabledTabItemProps
>) => {
  // `isHidden` is checked first: a tab that is both hidden and disabled must still be hidden. It
  // stays disabled too — `HiddenTab` forwards the prop — so it is not left selectable while invisible.
  if (isHidden)
    return (
      <HiddenTab id={id} isDisabled={isDisabled}>
        {children}
      </HiddenTab>
    );
  // `isDisabled` goes to the `Tab` itself, so react-aria gives the tab real disabled semantics —
  // `aria-disabled`, out of the arrow-key order, not selectable — and exposes `[data-disabled]` for
  // styling. Previously the prop was dropped and only a wrapper was styled, so a "disabled" tab was
  // still fully operable by mouse and keyboard.
  return (
    <StyledButtonMenuTabListItem key={id} id={id} isDisabled={isDisabled}>
      <ButtonMenuTabListItemContent>{children}</ButtonMenuTabListItemContent>
      {isDisabled && tooltipText ? <DisabledReason text={tooltipText} /> : null}
    </StyledButtonMenuTabListItem>
  );
};

/**
 * Why the disabled reason has an overlay element, and why that element takes no focus.
 *
 * The reason cannot be an attribute on the tab. react-aria-components' `Tab` filters its props
 * through `filterDOMProps(props, {global: true})` with no `labelable`, which drops
 * `aria-label`/`aria-labelledby`/`aria-describedby` before they reach the DOM. So the tooltip needs a
 * target of its own, and that target must not be focusable: a tab meant to be disabled containing a
 * tab stop is the defect this shape exists to avoid — the version that shipped first put an ordinary
 * `<button>` here and it announced as a second, nameless control.
 *
 * A non-focusable target that still shows a tooltip is what `PointerOnlyTooltip` is for, and this is
 * one of its two call sites — `navigation/underline-nav`'s coming-soon tab is the other, and uses
 * the same shape for the same reason (PLT-6309). The target is an empty span filling the tab, so hovering anywhere on the tab
 * reveals the reason and the bubble centres on the tab rather than on a corner of it. Hover reaches
 * it because the `[data-disabled]` rule below deliberately sets no `pointer-events: none`.
 *
 * The reason is therefore *also* rendered as visually hidden text, and that is the only route to it
 * for assistive tech and for the keyboard, neither of which a pointer-only tooltip reaches. The
 * accepted consequence: the reason joins the tab's accessible name rather than its description.
 * Announced once: the bubble keeps react-aria's `role="tooltip"` and is not aria-hidden, but nothing
 * points `aria-describedby` at it — a description is announced because something references it, and
 * here nothing does, so the visually hidden copy is the only thing read out. (Verified in the browser
 * on the PLT-6171 Storybook preview: hovering a disabled tab yields one `role="tooltip"` node and
 * zero `aria-describedby` attributes on the page.)
 *
 * This was `Tooltip` from `@primer/react` until PLT-6171. Primer's tooltip asserts that its trigger
 * matches `button:not([disabled])`, `a[href]`, an input or `[role="button"]`, so the overlay had to be
 * a real `<button>` held inert by `tabIndex={-1}`, `aria-hidden` and a mousedown that refused focus.
 * None of that is needed now, which is the point of the swap: the design-system tooltip has no
 * opinion about what the target is.
 */
const DisabledReason = ({ text }: { text: string }) => (
  <>
    <PointerOnlyTooltip text={text} placement="top" textAlsoRenderedForScreenReaders>
      <TooltipAnchor />
    </PointerOnlyTooltip>
    <VisuallyHidden>{text}</VisuallyHidden>
  </>
);

export const ButtonMenuTabListItem = ({
  children,
  id,
  isHidden,
}: React.PropsWithChildren<{
  id: React.ComponentProps<typeof Tab>['id'];
  isHidden?: boolean;
}>) => {
  if (isHidden) return <HiddenTab id={id}>{children}</HiddenTab>;
  return (
    <StyledButtonMenuTabListItem key={id} id={id}>
      <ButtonMenuTabListItemContent>{children}</ButtonMenuTabListItemContent>
    </StyledButtonMenuTabListItem>
  );
};

// Fills the tab, which is position: relative, and is both the hover area and the element the bubble
// is positioned against. Not styled onto the tooltip itself: the bubble is placed from JS against
// this box, so stretching the bubble over the tab would fight that and hide the label.
//
// A span rather than a button, which is the whole reason this file no longer needs Primer: it has no
// UA chrome to reset, takes no focus, and inherits the tab's not-allowed cursor. (No backticks in CSS
// comments here: the block is a styled-components template literal.)
const TooltipAnchor = styled.span`
  position: absolute;
  inset: 0;
`;

export const ButtonMenuTabContent = ({
  children,
  id,
}: React.PropsWithChildren<{ id?: React.ComponentProps<typeof TabPanel>['id'] }>) => {
  return <TabPanel id={id}>{children}</TabPanel>;
};

const StyledTabs = styled(Tabs)`
  width: 100%;
`;

const tabClass = 'button-menu-tab-list-item';
const tabContentClass = 'button-menu-tab-list-item-content';

const tabBorderRadius = '5px';
const pillBorderRadius = '50px';

type StyledListProps = {
  $collapseWidth?: string;
  $variant?: 'pill' | 'default';
  $fontSize?: 'xs' | 'sm';
  $tabPadding?: string;
  id?: string;
};

const pillVariantStyles = css<StyledListProps>`
  border-radius: ${pillBorderRadius};
  padding: var(--space__xs);
  border: 1px solid var(--color__gray-300);

  /* On a narrow screen the pill track stays one row and keeps every label whole: each tab is as wide
     as its label and never shrinks, and a set wider than the screen scrolls sideways inside its own
     track. Shrinking the tabs to share the width instead overlaps long labels into one unreadable
     string. The width: auto lines matter too: the default variant's collapse above sets every tab,
     and the selected one again, to the full track width, and a router tab (ButtonNavTabListItem,
     whose flex: none is inline) would keep it. The track starts at its left edge rather than
     centring, because an overflowing centred flex row puts its first tabs out of scroll reach. */
  @media (max-width: ${({ $collapseWidth }) => $collapseWidth || breakpoints[0]}) {
    flex-direction: row;
    justify-content: flex-start;
    overflow-x: auto;

    .${tabClass} {
      flex: 0 0 auto;
      width: auto;
      justify-content: center;
      font-size: var(--font-size__sm);

      &[data-selected] {
        width: auto;
      }
    }
  }

  .${tabClass} {
    border-radius: ${pillBorderRadius};
    padding: var(--space__xs) var(--space__sm);
    background-color: transparent;

    &:not(:first-child) {
      .${tabContentClass} {
        border-left: none;
        margin-left: 0;
      }
    }

    /* :not([data-selected]) for the same specificity reason as the default variant's hover rule
       above: without it this outranks the selected pill and replaces blue-100 with blue-50 under the
       pointer. */
    &:not([data-disabled]):not([data-selected]):hover {
      background-color: var(--color__blue-50);
      border-radius: ${pillBorderRadius};
    }

    /* The documented selected-pill pair: blue-700 on blue-100 (design/10-foundations.md
       "Selected tab segment"), 5.6:1. This was the active nav row's pair as well, borrowed from it;
       PLT-6076 repainted the nav row as a lilac pill, so blue now carries one meaning and this is
       it -- where you are inside a page, as against which page the sidebar rail has selected.
       What shipped before mixed the two palettes: --platform-* is the legacy theme.css scale and
       --color__* the design-system one, and --platform-blue-900 is a near-navy that read as a
       different intent again.
       (No backticks in these comments: the block is a styled-components template literal.) */
    &[data-selected] {
      color: var(--color__blue-700);
      background-color: var(--color__blue-100);
      border-radius: ${pillBorderRadius};
      border: none;
      margin: 0;
    }

    /* The default variant's focus ring re-declares the 5px tab radius at equal specificity, so
       without this the ring on a pill is drawn as a rectangle around a 50px-radius tab. */
    &[data-focus-visible] {
      border-radius: ${pillBorderRadius};
    }

    ${props =>
      props.$fontSize === 'xs' &&
      css`
        font-size: var(--font-size__sm);
      `}

    ${props =>
      props.$tabPadding &&
      css`
        padding: ${props.$tabPadding};
      `}
  }
`;

const StyledList = styled(TabList)<StyledListProps>`
  display: flex;
  justify-content: center;
  align-items: stretch;

  border-radius: ${tabBorderRadius};
  border: 1px solid var(--borderColor-default);
  width: fit-content;
  max-width: 100%;

  @media (max-width: ${props => props.$collapseWidth || breakpoints[0]}) {
    flex-direction: column;
  }

  .${tabClass} {
    position: relative;
    padding-top: var(--space__sm);
    padding-bottom: var(--space__sm);
    background-color: var(--color__gray-50);
    font-weight: 500;
    color: var(--fgColor-muted);
    width: max-content;
    flex: 0 1 auto;
    min-width: 0;
    cursor: pointer;
    border-radius: ${tabBorderRadius};
    white-space: nowrap;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;

    a {
      text-decoration: none;
      color: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* gray-100 is the documented hover tint (design/10-foundations.md, "Subtle backgrounds"),
       replacing a raw #ebedf0 that belonged to no scale. It is a ~2.5% darker step against this
       tab's own gray-000 rest fill, i.e. a slightly larger delta than the app shell's blessed
       white-to-gray-50 nav hover, so the affordance is not weakened by tokenising it.

       Both exclusions are load-bearing. :not() contributes its argument's specificity, so
       :not([data-disabled]):hover outranks the [data-selected] rule below instead of tying with it
       and losing on source order the way a bare :hover did — without :not([data-selected]) here,
       hovering the selected tab would repaint over its selected fill and take away the only cue
       for which tab you are on. */
    &:not([data-disabled]):not([data-selected]):hover {
      background-color: var(--color__gray-100);
      border-radius: ${tabBorderRadius};
    }

    /* Set by react-aria from the tab's own isDisabled. Deliberately no "pointer-events: none": the
       tab has to keep receiving hover so the disabled reason's tooltip can appear. The dimming goes
       on the label rather than the tab, because opacity applies to descendants — on the tab it would
       also wash out that tooltip. */
    &[data-disabled] {
      cursor: not-allowed;

      .${tabContentClass} {
        opacity: 0.5;
      }
    }

    .${tabContentClass} {
      padding-left: var(--space__sm);
      padding-right: var(--space__sm);
    }

    ${props =>
      props.$fontSize === 'xs' &&
      css`
        font-size: var(--font-size__sm);
      `}
    ${props =>
      props.$tabPadding &&
      css`
        padding: ${props.$tabPadding};
      `}
        &:not(:first-child) {
      .${tabContentClass} {
        border-left: 1px solid var(--borderColor-default);
        margin-left: -1px;
      }
    }

    &[data-selected] {
      color: var(--fgColor-default);
      background-color: var(--bgColor-default);
      border: 1px solid var(--fgColor-default);

      margin-left: -1px;
      margin-right: -1px;
      margin-top: -1px;
      margin-bottom: -1px;

      border-radius: ${tabBorderRadius};
      z-index: 1;

      .${tabContentClass} {
        border: none;
        margin-left: 0;
      }
    }

    &[data-focus-visible] {
      outline: 2px solid var(--focus-outlineColor);
      z-index: 2;
      outline-offset: -1px;
      border-radius: ${tabBorderRadius};

      .${tabContentClass} {
        border: none;
        margin-left: 0;
      }
    }

    @media (max-width: ${props => props.$collapseWidth || breakpoints[0]}) {
      width: 100%;
      &[data-selected] {
        width: calc(100% + 2px);
        margin-left: -1px;
        margin-right: -1px;
        margin-top: -1px;
        margin-bottom: -1px;
      }

      .${tabContentClass} {
        border-left: none;
        margin-left: 0;
      }
    }
  }

  /* Apply the overrides if the variant is 'pill' */
  ${({ $variant }) => $variant === 'pill' && pillVariantStyles}
`;

export const StyledButtonMenuTabListItem = styled(Tab).attrs({
  className: tabClass,
})``;

export const ButtonMenuTabListItemContent = styled.div.attrs({
  className: tabContentClass,
})`
  text-align: center;
  width: fit-content;
`;
